/**
 * Tutorial Helper Utilities
 * 
 * Provides utility functions for tutorial step management, validation,
 * and integration with the tutorial system.
 * 
 * Requirements: 1.4, 1.5, 2.4, 6.2
 */

import type { TutorialStep, TutorialStepCollection } from '../types/tutorial';
import type { ResponsiveTutorialStep } from './responsiveTutorial';
import { mainPageSteps, categoryPageSteps, getTutorialSteps } from '../config/tutorialSteps';

/**
 * Validate tutorial step configuration with enhanced error reporting
 * 
 * Ensures all required properties are present and valid for react-joyride.
 * 
 * @param step - Tutorial step to validate
 * @returns Validation result with detailed error information
 */
export function validateTutorialStep(step: TutorialStep): { 
  isValid: boolean; 
  errors: string[];
  warnings: string[];
} {
  const errors: string[] = [];
  const warnings: string[] = [];

  // Required properties
  if (!step.id) {
    errors.push('Step must have a unique id');
  }

  if (!step.target) {
    errors.push('Step must have a target selector');
  }

  if (!step.content) {
    errors.push('Step must have content');
  }

  if (!step.section) {
    errors.push('Step must specify a section');
  }

  // Validate target selector format
  if (step.target && typeof step.target === 'string') {
    try {
      // Test if selector is valid by attempting to use it
      document.querySelector(step.target);
    } catch (error) {
      errors.push(`Invalid CSS selector: ${step.target}`);
    }

    // Warn about potentially problematic selectors
    if (!step.target.startsWith('.') && !step.target.startsWith('#') && !step.target.match(/^[a-zA-Z]/)) {
      warnings.push(`Target selector "${step.target}" may not be reliable. Consider using class or ID selectors.`);
    }
  }

  // Validate placement
  const validPlacements = ['top', 'bottom', 'left', 'right', 'center'];
  if (step.placement && !validPlacements.includes(step.placement)) {
    errors.push(`Invalid placement: ${step.placement}. Must be one of: ${validPlacements.join(', ')}`);
  }

  // Validate content
  if (step.content && typeof step.content === 'string' && step.content.trim().length === 0) {
    warnings.push('Step content is empty or only whitespace');
  }

  // Validate title
  if (step.title && typeof step.title === 'string' && step.title.trim().length === 0) {
    warnings.push('Step title is empty or only whitespace');
  }

  return {
    isValid: errors.length === 0,
    errors,
    warnings,
  };
}

/**
 * Validate all tutorial steps in a collection
 * 
 * @param steps - Array of tutorial steps to validate
 * @returns Validation summary with any errors
 */
export function validateTutorialSteps(steps: TutorialStep[]): { 
  isValid: boolean; 
  errors: Array<{ stepId: string; errors: string[] }>;
  duplicateIds: string[];
} {
  const stepErrors: Array<{ stepId: string; errors: string[] }> = [];
  const seenIds = new Set<string>();
  const duplicateIds: string[] = [];

  for (const step of steps) {
    // Check for duplicate IDs
    if (seenIds.has(step.id)) {
      duplicateIds.push(step.id);
    } else {
      seenIds.add(step.id);
    }

    // Validate individual step
    const validation = validateTutorialStep(step);
    if (!validation.isValid) {
      stepErrors.push({
        stepId: step.id,
        errors: validation.errors,
      });
    }
  }

  return {
    isValid: stepErrors.length === 0 && duplicateIds.length === 0,
    errors: stepErrors,
    duplicateIds,
  };
}

/**
 * Filter tutorial steps based on criteria
 * 
 * @param steps - Array of tutorial steps
 * @param criteria - Filtering criteria
 * @returns Filtered array of tutorial steps
 */
export function filterTutorialSteps(
  steps: TutorialStep[],
  criteria: {
    required?: boolean;
    section?: string;
    hasTarget?: boolean;
  }
): TutorialStep[] {
  return steps.filter(step => {
    if (criteria.required !== undefined && step.required !== criteria.required) {
      return false;
    }

    if (criteria.section && step.section !== criteria.section) {
      return false;
    }

    if (criteria.hasTarget !== undefined) {
      const hasTarget = Boolean(step.target);
      if (hasTarget !== criteria.hasTarget) {
        return false;
      }
    }

    return true;
  });
}

/**
 * Get tutorial steps organized by section
 * 
 * @returns Complete tutorial step collection organized by section
 */
export function getTutorialStepCollection(): TutorialStepCollection {
  return {
    main: mainPageSteps,
    categories: categoryPageSteps,
    account: [], // Placeholder for future account page tutorial
    dashboard: [], // Placeholder for future dashboard tutorial
  };
}

/**
 * Check if a DOM element exists for a tutorial step target with enhanced error handling
 * 
 * @param target - CSS selector for the target element
 * @param options - Additional options for element checking
 * @returns Whether the target element exists and is visible in the DOM
 */
export function isTargetElementAvailable(
  target: string, 
  options: {
    checkVisibility?: boolean;
    checkInteractable?: boolean;
    timeout?: number;
  } = {}
): boolean {
  const { checkVisibility = true, checkInteractable = false } = options;

  try {
    const element = document.querySelector(target);
    
    if (!element) {
      return false;
    }

    // Check if element is visible
    if (checkVisibility) {
      const rect = element.getBoundingClientRect();
      const style = window.getComputedStyle(element);
      
      // Element is not visible if:
      // - It has no dimensions
      // - It's hidden via CSS
      // - It's outside the viewport (with some tolerance)
      if (
        rect.width === 0 || 
        rect.height === 0 ||
        style.display === 'none' ||
        style.visibility === 'hidden' ||
        style.opacity === '0' ||
        rect.bottom < -100 ||
        rect.top > window.innerHeight + 100
      ) {
        return false;
      }
    }

    // Check if element is interactable
    if (checkInteractable) {
      const style = window.getComputedStyle(element);
      if (style.pointerEvents === 'none' || (element as HTMLInputElement).disabled) {
        return false;
      }
    }

    return true;
  } catch (error) {
    console.warn(`Error checking target element availability: ${target}`, error);
    return false;
  }
}

/**
 * Filter tutorial steps to only include those with available target elements
 * Enhanced with better error handling and logging
 * 
 * @param steps - Array of tutorial steps
 * @param options - Filtering options
 * @returns Steps with available target elements
 */
export function filterAvailableSteps(
  steps: TutorialStep[], 
  options: {
    checkVisibility?: boolean;
    checkInteractable?: boolean;
    logMissing?: boolean;
  } = {}
): TutorialStep[] {
  const { checkVisibility = true, checkInteractable = false, logMissing = true } = options;
  const availableSteps: TutorialStep[] = [];
  const missingSteps: string[] = [];

  for (const step of steps) {
    if (!step.target || typeof step.target !== 'string') {
      // Include steps without specific targets (like center-placed steps)
      availableSteps.push(step);
      continue;
    }

    const isAvailable = isTargetElementAvailable(step.target, {
      checkVisibility,
      checkInteractable,
    });

    if (isAvailable) {
      availableSteps.push(step);
    } else {
      missingSteps.push(`${step.id}: ${step.target}`);
    }
  }

  if (logMissing && missingSteps.length > 0) {
    console.warn(
      `[TutorialHelpers] ${missingSteps.length} tutorial steps have unavailable targets:`,
      missingSteps
    );
  }

  return availableSteps;
}

/**
 * Get tutorial progress information
 * 
 * @param currentStepIndex - Current step index (0-based)
 * @param totalSteps - Total number of steps
 * @returns Progress information
 */
export function getTutorialProgress(currentStepIndex: number, totalSteps: number): {
  percentage: number;
  current: number;
  total: number;
  isComplete: boolean;
} {
  const current = Math.max(0, Math.min(currentStepIndex + 1, totalSteps));
  const percentage = totalSteps > 0 ? Math.round((current / totalSteps) * 100) : 0;

  return {
    percentage,
    current,
    total: totalSteps,
    isComplete: current >= totalSteps,
  };
}

/**
 * Generate tutorial step analytics data
 * 
 * @param step - Tutorial step
 * @param action - Action taken on the step
 * @returns Analytics data object
 */
export function generateStepAnalytics(
  step: TutorialStep,
  action: 'view' | 'next' | 'previous' | 'skip' | 'complete'
): {
  stepId: string;
  section: string;
  action: string;
  required: boolean;
  timestamp: number;
} {
  return {
    stepId: step.id,
    section: step.section,
    action,
    required: step.required ?? true,
    timestamp: Date.now(),
  };
}

/**
 * Create tutorial step with default values
 * 
 * @param stepData - Partial tutorial step data
 * @returns Complete tutorial step with defaults applied
 */
export function createTutorialStep(stepData: Partial<TutorialStep> & { 
  id: string; 
  target: string; 
  content: React.ReactNode; 
}): TutorialStep {
  return {
    placement: 'bottom',
    section: 'main',
    required: true,
    disableBeacon: false,
    hideCloseButton: false,
    hideFooter: false,
    ...stepData,
  };
}

/**
 * Get next available step index with enhanced error handling
 * 
 * Skips steps whose target elements are not available in the DOM.
 * 
 * @param steps - Array of tutorial steps
 * @param currentIndex - Current step index
 * @param direction - Direction to search ('next' or 'previous')
 * @param options - Additional options for step checking
 * @returns Next available step index or -1 if none found
 */
export function getNextAvailableStepIndex(
  steps: TutorialStep[],
  currentIndex: number,
  direction: 'next' | 'previous' = 'next',
  options: {
    checkVisibility?: boolean;
    maxAttempts?: number;
  } = {}
): number {
  const { checkVisibility = true, maxAttempts = steps.length } = options;
  const increment = direction === 'next' ? 1 : -1;
  let nextIndex = currentIndex + increment;
  let attempts = 0;

  while (nextIndex >= 0 && nextIndex < steps.length && attempts < maxAttempts) {
    const step = steps[nextIndex];
    
    // If step has no specific target or target is available, use this step
    if (!step.target || typeof step.target !== 'string') {
      return nextIndex;
    }

    if (isTargetElementAvailable(step.target, { checkVisibility })) {
      return nextIndex;
    }

    nextIndex += increment;
    attempts++;
  }

  return -1; // No available step found
}

/**
 * Wait for target element to become available with enhanced error handling
 * 
 * @param target - CSS selector for target element
 * @param options - Wait options
 * @returns Promise that resolves when element is available or rejects on timeout
 */
export function waitForTargetElement(
  target: string,
  options: {
    timeout?: number;
    interval?: number;
    checkVisibility?: boolean;
    onProgress?: (elapsed: number) => void;
  } = {}
): Promise<Element> {
  const { 
    timeout = 5000, 
    interval = 100, 
    checkVisibility = true,
    onProgress 
  } = options;

  return new Promise((resolve, reject) => {
    const startTime = Date.now();
    let lastProgressUpdate = 0;

    const checkElement = () => {
      const elapsed = Date.now() - startTime;
      
      // Call progress callback every 500ms
      if (onProgress && elapsed - lastProgressUpdate >= 500) {
        onProgress(elapsed);
        lastProgressUpdate = elapsed;
      }

      try {
        const element = document.querySelector(target);
        
        if (element) {
          // Additional visibility check if requested
          if (checkVisibility) {
            const rect = element.getBoundingClientRect();
            const style = window.getComputedStyle(element);
            
            if (
              rect.width > 0 && 
              rect.height > 0 &&
              style.display !== 'none' &&
              style.visibility !== 'hidden'
            ) {
              resolve(element);
              return;
            }
          } else {
            resolve(element);
            return;
          }
        }

        if (elapsed >= timeout) {
          reject(new Error(
            `Target element "${target}" not found within ${timeout}ms. ` +
            `Element ${element ? 'exists but is not visible' : 'does not exist'}.`
          ));
          return;
        }

        setTimeout(checkElement, interval);
      } catch (error) {
        reject(new Error(
          `Error checking target element "${target}": ${error instanceof Error ? error.message : String(error)}`
        ));
      }
    };

    checkElement();
  });
}

/**
 * Prepare tutorial steps for react-joyride with enhanced error handling
 * 
 * Validates steps, filters available targets, and applies default styling.
 * 
 * @param section - Tutorial section
 * @param options - Preparation options
 * @returns Prepared tutorial steps ready for react-joyride
 */
export function prepareTutorialSteps(
  section: 'main' | 'categories' | 'combined',
  options: {
    filterUnavailable?: boolean;
    requiredOnly?: boolean;
    validateSteps?: boolean;
    checkVisibility?: boolean;
    retryMissing?: boolean;
    maxRetries?: number;
  } = {}
): ResponsiveTutorialStep[] {
  const {
    filterUnavailable = true,
    requiredOnly = false,
    validateSteps = true,
    checkVisibility = true,
    retryMissing = false,
    maxRetries = 3,
  } = options;

  try {
    let steps = getTutorialSteps(section);

    if (!steps || steps.length === 0) {
      console.warn(`[TutorialHelpers] No tutorial steps found for section: ${section}`);
      return [];
    }

    // Filter to required steps only if requested
    if (requiredOnly) {
      steps = filterTutorialSteps(steps, { required: true });
    }

    // Filter to available target elements if requested
    if (filterUnavailable) {
      const originalCount = steps.length;
      steps = filterAvailableSteps(steps, { 
        checkVisibility,
        logMissing: true,
      });
      
      if (steps.length < originalCount) {
        console.warn(
          `[TutorialHelpers] Filtered out ${originalCount - steps.length} steps with unavailable targets`
        );
      }

      // If we have very few steps left and retries are enabled, log a warning
      if (retryMissing && steps.length < originalCount * 0.5 && maxRetries > 0) {
        console.warn('[TutorialHelpers] Many steps filtered due to missing targets. Consider checking if UI elements are properly loaded.');
      }
    }

    // Validate steps if requested
    if (validateSteps) {
      const validation = validateTutorialSteps(steps);
      if (!validation.isValid) {
        console.warn('[TutorialHelpers] Tutorial step validation failed:', validation);
        
        // Filter out invalid steps
        const validSteps = steps.filter(step => {
          const stepValidation = validateTutorialStep(step);
          return stepValidation.isValid;
        });
        
        if (validSteps.length !== steps.length) {
          console.warn(
            `[TutorialHelpers] Filtered out ${steps.length - validSteps.length} invalid steps`
          );
          steps = validSteps;
        }
      }
    }

    if (steps.length === 0) {
      console.warn(`[TutorialHelpers] No valid tutorial steps available for section: ${section}`);
    } else {
      console.log(`[TutorialHelpers] Prepared ${steps.length} tutorial steps for section: ${section}`);
    }

    return steps;
  } catch (error) {
    console.error(`[TutorialHelpers] Error preparing tutorial steps for section ${section}:`, error);
    return [];
  }
}